// client/src/components/CustomerServiceDashboard.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import OrderList from "./OrderList";
import Modal from "react-modal";

// For react-modal accessibility
Modal.setAppElement('#root');

const CustomerServiceDashboard = () => {
  const [activeTab, setActiveTab] = useState("registered");
  const [registeredOrders, setRegisteredOrders] = useState([]);
  const [directOrders, setDirectOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal for full view
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const [regRes, directRes] = await Promise.all([
        axios.get("/api/orders/customer-service-orders"),
        axios.get("/api/direct-orders/all"),
      ]);
      setRegisteredOrders(regRes.data);
      setDirectOrders(directRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFullView = (order) => {
    setSelectedOrder(order);
    setIsViewModalOpen(true);
  };

  const closeModal = () => {
    setIsViewModalOpen(false);
    setSelectedOrder(null);
  };

  return (
    <div className="p-2 sm:p-6">
      <h2 className="text-3xl font-bold mb-6">Customer Service Dashboard</h2>

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-6">
        <button
          onClick={() => setActiveTab("registered")}
          className={`px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm rounded ${activeTab === "registered" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
        >
          Registered Orders
        </button>
        <button
          onClick={() => setActiveTab("direct")}
          className={`px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm rounded ${activeTab === "direct" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
        >
          Online Orders
        </button>
      </div>

      {loading ? (
        <p>Loading orders…</p>
      ) : activeTab === "registered" ? (
        <OrderList orders={registeredOrders} role="customer-service" />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full bg-white">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Image</th>
                <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Order ID</th>
                <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Product ID</th>
                <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Date</th>
                <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Customer</th>
                <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Phone</th>
                <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Address</th>
                <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Total (USD)</th>
                <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Status</th>
                <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {directOrders.map(order => (
                <tr key={order._id} className={`border-b ${order.items.length > 1 ? "bg-yellow-50" : ""}`}>
                  <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">
                    {order.items?.[0]?.photo ? (
                      <img
                        src={`/uploads/${order.items[0].photo.replace(/\\/g, "/")}`}
                        alt="Product"
                        className="w-12 h-12 object-cover rounded border"
                      />
                    ) : "—"}
                  </td>
                  <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">{order.orderId}</td>
                  <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">
                    {order.items.length === 1 ? order.items[0].productId : "Multiple"}
                  </td>
                  <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">{new Date(order.createdAt).toLocaleDateString()}</td>
                  <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">{order.customer?.name}</td>
                  <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">{order.customer?.phone}</td>
                  <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">{order.customer?.address}</td>
                  <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">
                    {order.totalAmount !== undefined ? `$${order.totalAmount.toFixed(2)}` : "—"}
                  </td>
                  <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">
                    <select
                      value={order.status}
                      onChange={async (e) => {
                        await axios.put(`/api/direct-orders/update/${order._id}`, {
                          status: e.target.value,
                        });
                        fetchOrders();
                      }}
                      className="border p-1 rounded"
                    >
                      <option>Pending</option>
                      <option>Processing</option>
                      <option>Delivering</option>
                      <option>Delivered</option>
                      <option>Cancelled</option>
                    </select>
                  </td>
                  <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm flex gap-2">
                    <button
                      className="px-2 py-1 bg-green-600 text-white rounded"
                      onClick={() =>
                        window.open(`/invoice/direct/${order.orderId}`, "_blank")
                      }
                    >
                      Print Invoice
                    </button>
                    <button
                      className="px-2 py-1 bg-blue-500 text-white rounded relative"
                      onClick={() => handleFullView(order)}
                    >
                      View
                      {order.items.length > 1 && (
                        <>
                          <span className="animate-ping absolute -top-1 -right-1 inline-flex h-2 w-2 rounded-full bg-red-500 opacity-75"></span>
                          <span className="absolute -top-1 -right-1 inline-flex rounded-full h-2 w-2 bg-red-600"></span>
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>

          </table>
        </div>
      )}

      {/* Full View Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onRequestClose={closeModal}
        style={{ content: { minWidth: "0" } }}
        className="p-2 sm:p-6 bg-white rounded-lg shadow-lg w-full max-w-3xl mx-auto mt-10 overflow-auto"

        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
      >
        {selectedOrder && (
          <div className="min-h-[600px]">
            <h2 className="text-2xl font-bold mb-4">Order Full Details ({selectedOrder.orderId})</h2>

            <div className="mb-4 grid grid-cols-2 gap-4 text-sm">
              <div><strong>Status:</strong> {selectedOrder.status}</div>
              <div><strong>Customer:</strong> {selectedOrder.customer?.name || "—"}</div>
              <div><strong>Phone:</strong> {selectedOrder.customer?.phone || "—"}</div>
              <div><strong>Address:</strong> {selectedOrder.customer?.address || "—"}</div>
              <div><strong>Total:</strong> ${selectedOrder.totalAmount?.toFixed(2) || "—"}</div>
            </div>

            <table className="w-full text-sm mb-4">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Image</th>
                  <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Product ID</th> {/* ✅ added */}
                  <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Name</th>
                  <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Weight</th>
                  <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Quantity</th>
                  <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs sm:text-sm">Price</th>
                </tr>
              </thead>
              <tbody>
                {selectedOrder.items.map(item => (
                  <tr key={item._id}>
                    <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">
                      <img
                        src={`/uploads/${item.photo.replace(/\\/g, '/')}`}
                        className="w-12 h-12 object-cover rounded"
                      />
                    </td>
                    <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">{item.productId}</td> {/* ✅ added */}
                    <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">{item.name}</td>
                    <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">{item.weight}g</td>
                    <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">{item.quantity || 1}</td>
                    <td className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">${item.price.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>


            <div className="mt-4 flex flex-col sm:flex-row justify-end gap-2">
              <button
                className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm rounded"
                onClick={() => window.print()}
              >
                Print Order
              </button>
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm rounded"
                onClick={closeModal}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default CustomerServiceDashboard;
